import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostBinding,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { AccountAppValue, RecoveryQuestion } from "@fabric-types/app-account";
import { select, Store } from "@ngrx/store";
import { CommonState } from "@obg/ngx.common";
import { CORE_ROUTE, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { TelemetryService, DocumentRef } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { combineLatest, merge, Observable, Subscription } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { LoginAction, LoginSelector, ResetPasswordModel } from "../../../sdk";
import { ForgotPasswordService } from "../forgot-password.service";

const USERNAME_CONTROL_NAME = "username";

@Component({
	selector: "obg-m-forgot-password-email-container",
	templateUrl: "./forgot-password-email.container.html",
	styleUrls: ["./forgot-password-email.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [ForgotPasswordService],
})
export class ForgotPasswordEmailContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-forgot-password-email") hostCssClass = true;

	@Output() isBusy = new EventEmitter<boolean>();
	@Output() success = new EventEmitter();

	formGroup: UntypedFormGroup;
	hasFailed$: Observable<boolean> | undefined;
	isSgaJurisdiction$: Observable<Jurisdiction | undefined> | undefined;

	loginRouteLink = CORE_ROUTE.auth.login;

	recoveryQuestions: RecoveryQuestion[] | undefined;

	private data$$: Subscription | undefined;
	private hasSucceeded$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState>,
		private formBuilder: UntypedFormBuilder,
		private loginAction: LoginAction,
		private telemetryService: TelemetryService,
		private forgotPasswordService: ForgotPasswordService,
		private documentRef: DocumentRef
	) {
		this.formGroup = this.formBuilder.group({
			[USERNAME_CONTROL_NAME]: this.formBuilder.control("", [Validators.required, OBG_VALIDATORS.email]),
		});
	}

	ngOnInit() {
		const setUsername$ = this.forgotPasswordService.setUsername$(this.formGroup.controls[USERNAME_CONTROL_NAME]);

		this.isSgaJurisdiction$ = this.isJurisdiction(Jurisdiction.Sga);

		this.hasFailed$ = combineLatest([
			this.store.select(LoginSelector.isForgotPasswordByEmailFailed),
			this.store.select(LoginSelector.isForgotPasswordByEmailAndSecurityQuestionFailed),
		]).pipe(
			map(([emailFailed, emailAndSecurityQuestionFailed]) => emailFailed || emailAndSecurityQuestionFailed),
			filter((hasFailed) => hasFailed),
			tap(() => {
				this.isBusy.emit(false);
				this.formGroup.enable();
			})
		);

		const recoveryQuestions$ = new Observable<RecoveryQuestion[]>(
			this.documentRef.obgEventBus.retrieve(AccountAppValue.GetRecoveryQuestions)
		).pipe(tap((recoveryQuestions) => (this.recoveryQuestions = recoveryQuestions)));

		this.data$$ = merge(setUsername$, this.isSgaJurisdiction$, this.hasFailed$, recoveryQuestions$).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	submit(data: ResetPasswordModel) {
		if (this.formGroup.invalid || this.formGroup.disabled) {
			return;
		}

		this.telemetryService.track({
			event: GtmEventAction.LoginPasswordFormSubmit,
		});

		this.isBusy.emit(true);

		if (this.hasSucceeded$$) {
			this.hasSucceeded$$.unsubscribe();
			this.hasSucceeded$$ = undefined;
		}

		this.hasSucceeded$$ = combineLatest([
			this.store.select(LoginSelector.isForgotPasswordByEmailSuccess),
			this.store.select(LoginSelector.isForgotPasswordByEmailAndSecurityQuestionSuccess),
		])
			.pipe(
				map(
					([emailSuccess, emailAndSecurityQuestionSuccess]) => emailSuccess || emailAndSecurityQuestionSuccess
				),
				filter((isSuccess) => isSuccess),
				take(1),
				tap(() => {
					this.isBusy.emit(false);
					this.telemetryService.track({
						event: GtmEventAction.ForgottenPasswordSuccess,
					});
					this.success.emit();
				})
			)
			.subscribe();

		this.formGroup.disable();
		this.store.dispatch(this.loginAction.resetPasswordByEmail(data.username));
	}

	isJurisdiction(jurisdiction: Jurisdiction) {
		return this.store.pipe(
			select(JurisdictionSelector.get),
			filter((currentJurisdiction) => currentJurisdiction === jurisdiction)
		);
	}
}
